import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { formatDate } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { fetchOrderDetail, deleteOrder } from '@/services/orders'
import type { Order, OrderStatus } from '@/types/orders'
import {
  AlertCircle,
  ArrowLeft,
  Phone,
  Calendar,
  DollarSign,
  Package,
  User,
  Ruler,
  Scale,
  Clock,
  CheckCircle2,
  CircleDashed,
  XCircle,
  PackageX,
  Trash2,
} from 'lucide-react'

const ORDER_STATUS_CONFIG: Record<OrderStatus, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode; labelKey: string }> = {
  new: { variant: 'default', icon: <CircleDashed className="h-4 w-4" />, labelKey: 'orders.new' },
  in_progress: { variant: 'secondary', icon: <Clock className="h-4 w-4" />, labelKey: 'orders.inProgress' },
  completed: { variant: 'outline', icon: <CheckCircle2 className="h-4 w-4" />, labelKey: 'orders.completed' },
  cancelled: { variant: 'destructive', icon: <XCircle className="h-4 w-4" />, labelKey: 'orders.cancelled' },
}

function LoadingSkeleton() {
  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader>
          <div className='flex items-center gap-4'>
            <Skeleton className='h-16 w-16 rounded-full' />
            <div className='space-y-2'>
              <Skeleton className='h-6 w-48' />
              <Skeleton className='h-4 w-32' />
            </div>
          </div>
        </CardHeader>
      </Card>
      <div className='grid gap-4 md:grid-cols-4'>
        <Skeleton className='h-32' />
        <Skeleton className='h-32' />
        <Skeleton className='h-32' />
        <Skeleton className='h-32' />
      </div>
    </div>
  )
}

export default function OrderDetail() {
  const { t, i18n } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [order, setOrder] = useState<Order | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  useEffect(() => {
    const loadOrder = async () => {
      if (!id) return

      try {
        setIsLoading(true)
        setError(null)
        setNotFound(false)
        const data = await fetchOrderDetail(parseInt(id))
        setOrder(data)
      } catch (err: unknown) {
        const axiosError = err as { response?: { data?: { message?: string } } }
        const message = axiosError?.response?.data?.message

        if (message?.toLowerCase().includes('no order matches')) {
          setNotFound(true)
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load order details')
        }
      } finally {
        setIsLoading(false)
      }
    }

    loadOrder()
  }, [id])

  const handleDelete = async () => {
    if (!id) return

    try {
      setIsDeleting(true)
      setError(null)
      await deleteOrder(parseInt(id))
      navigate('/orders')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete order')
      setIsDeleting(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(amount)
  }

  return (
    <>
      <header className='flex h-16 shrink-0 items-center gap-2'>
        <div className='flex items-center gap-2 px-4'>
          <SidebarTrigger className='-ml-1' />
          <Separator
            orientation='vertical'
            className='mr-2 data-[orientation=vertical]:h-4'
          />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to='/orders'>{t('orders.title')}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  {order ? t('orders.orderNumber', { id: order.id }) : t('common.loading')}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </header>

      {error && (
        <div className='mx-4 mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400'>
          <AlertCircle className='h-4 w-4' />
          <span>{error}</span>
        </div>
      )}

      <div className='flex flex-1 flex-col gap-4 p-4 pt-0'>
        <div className='flex items-center gap-2'>
          <Button variant='ghost' size='sm' asChild>
            <Link to='/orders'>
              <ArrowLeft className='mr-2 h-4 w-4' />
              {t('orders.backToOrders')}
            </Link>
          </Button>
        </div>

        {isLoading ? (
          <LoadingSkeleton />
        ) : order ? (
          <>
            {/* Order Header Card */}
            <Card>
              <CardHeader>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-6'>
                    <div className='flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary'>
                      <Package className='h-8 w-8' />
                    </div>
                    <div className='space-y-1'>
                      <CardTitle className='text-2xl'>
                        Order #{order.id}
                      </CardTitle>
                      <div className='text-lg font-medium text-primary'>
                        {order.material.name} {order.square} {order.height} {order.width} {order.quantity}
                      </div>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Badge variant={ORDER_STATUS_CONFIG[order.order_status].variant} className='gap-1 text-sm px-3 py-1'>
                      {ORDER_STATUS_CONFIG[order.order_status].icon}
                      {t(ORDER_STATUS_CONFIG[order.order_status].labelKey)}
                    </Badge>
                    <Button
                      variant='destructive'
                      size='sm'
                      onClick={() => setIsDeleteDialogOpen(true)}
                    >
                      <Trash2 className='mr-2 h-4 w-4' />
                      {t('common.delete')}
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Stats Cards */}
            <div className='grid gap-4 md:grid-cols-4'>
              <Card>
                <CardHeader className='flex flex-row items-center justify-between pb-2'>
                  <CardTitle className='text-sm font-medium'>{t('orders.fullPrice')}</CardTitle>
                  <DollarSign className='h-4 w-4 text-green-500' />
                </CardHeader>
                <CardContent>
                  <div className='text-2xl font-bold text-green-600 dark:text-green-400'>
                    {formatCurrency(order.full_price)}
                  </div>
                  <p className='text-xs text-muted-foreground mt-1'>
                    {t('orders.unitPrice')}: {formatCurrency(order.price)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className='flex flex-row items-center justify-between pb-2'>
                  <CardTitle className='text-sm font-medium'>{t('orders.paid')}</CardTitle>
                  <DollarSign className='h-4 w-4 text-blue-500' />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${order.paid >= order.full_price ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}`}>
                    {formatCurrency(order.paid)}
                  </div>
                  <p className='text-xs text-muted-foreground mt-1'>
                    {t('orders.prePayment')}: {formatCurrency(order.pre_payment)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className='flex flex-row items-center justify-between pb-2'>
                  <CardTitle className='text-sm font-medium'>{t('orders.remaining')}</CardTitle>
                  <DollarSign className='h-4 w-4 text-amber-500' />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${order.full_price - order.paid > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                    {formatCurrency(order.full_price - order.paid)}
                  </div>
                  <p className='text-xs text-muted-foreground mt-1'>
                    {order.full_price - order.paid > 0 ? t('orders.amountDue') : t('orders.fullyPaid')}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className='flex flex-row items-center justify-between pb-2'>
                  <CardTitle className='text-sm font-medium'>{t('orders.progress')}</CardTitle>
                  <Package className='h-4 w-4 text-muted-foreground' />
                </CardHeader>
                <CardContent>
                  <div className='text-2xl font-bold'>
                    {order.done_quantity} / {order.quantity}
                  </div>
                  <p className='text-xs text-muted-foreground mt-1'>
                    {t('orders.itemsCompleted')}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Details Cards */}
            <div className='grid gap-4 md:grid-cols-2'>
              {/* Client Info */}
              <Card>
                <CardHeader className='pb-3'>
                  <CardTitle className='flex items-center gap-2 text-sm font-medium'>
                    <User className='h-4 w-4' />
                    {t('orders.clientInformation')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-3'>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('orders.fullName')}</p>
                      <p className='text-sm font-medium'>{order.client.full_name}</p>
                    </div>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('orders.phoneNumber')}</p>
                      <p className='text-sm font-medium flex items-center gap-2'>
                        <Phone className='h-3 w-3' />
                        {order.client.phone_number}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Material Info */}
              <Card>
                <CardHeader className='pb-3'>
                  <CardTitle className='flex items-center gap-2 text-sm font-medium'>
                    <Package className='h-4 w-4' />
                    {t('orders.materialDetails')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-3'>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('orders.material')}</p>
                      <p className='text-sm font-medium'>{order.material.name} ({t('orders.thickness')}: {order.material.thickness})</p>
                    </div>
                    <div className='flex gap-4'>
                      <div>
                        <p className='text-sm text-muted-foreground'>{t('orders.color')}</p>
                        <p className='text-sm font-medium'>{order.color.name}</p>
                      </div>
                      <div>
                        <p className='text-sm text-muted-foreground'>{t('orders.wireType')}</p>
                        <p className='text-sm font-medium'>{order.wire_type.name}</p>
                      </div>
                      <div>
                        <p className='text-sm text-muted-foreground'>{t('orders.sellingType')}</p>
                        <p className='text-sm font-medium'>{order.selling_type.name}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Dimensions */}
              <Card>
                <CardHeader className='pb-3'>
                  <CardTitle className='flex items-center gap-2 text-sm font-medium'>
                    <Ruler className='h-4 w-4' />
                    {t('orders.dimensionsAndQuantity')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='grid grid-cols-2 gap-3'>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('orders.width')}</p>
                      <p className='text-sm font-medium'>{order.width} m</p>
                    </div>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('orders.height')}</p>
                      <p className='text-sm font-medium'>{order.height} m</p>
                    </div>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('orders.square')}</p>
                      <p className='text-sm font-medium'>{order.square} m²</p>
                    </div>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('orders.quantity')}</p>
                      <p className='text-sm font-medium'>{order.quantity}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Weight */}
              <Card>
                <CardHeader className='pb-3'>
                  <CardTitle className='flex items-center gap-2 text-sm font-medium'>
                    <Scale className='h-4 w-4' />
                    {t('orders.weightInformation')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='grid grid-cols-2 gap-3'>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('orders.currentWeight')}</p>
                      <p className='text-sm font-medium'>{order.weight} kg</p>
                    </div>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('orders.requiredWeight')}</p>
                      <p className='text-sm font-medium'>{order.must_weight} kg</p>
                    </div>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('orders.profit')}</p>
                      <p className='text-sm font-medium'>{formatCurrency(order.profit)}</p>
                    </div>
                    {order.deadline && (
                      <div>
                        <p className='text-sm text-muted-foreground'>{t('orders.deadline')}</p>
                        <p className='text-sm font-medium'>{formatDate(order.deadline)}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Created By */}
              <Card>
                <CardHeader className='pb-3'>
                  <CardTitle className='flex items-center gap-2 text-sm font-medium'>
                    <User className='h-4 w-4' />
                    {t('orders.createdBy')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-3'>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('orders.name')}</p>
                      <p className='text-sm font-medium'>{order.created_by.first_name} {order.created_by.last_name}</p>
                    </div>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('orders.email')}</p>
                      <p className='text-sm font-medium'>{order.created_by.email}</p>
                    </div>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('orders.role')}</p>
                      <Badge variant='outline' className='capitalize'>{order.created_by.role}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Timestamps */}
              <Card>
                <CardHeader className='pb-3'>
                  <CardTitle className='flex items-center gap-2 text-sm font-medium'>
                    <Calendar className='h-4 w-4' />
                    {t('orders.timestamps')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-3'>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('orders.created')}</p>
                      <p className='text-sm font-medium'>{formatDate(order.created_at)}</p>
                    </div>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('orders.lastUpdated')}</p>
                      <p className='text-sm font-medium'>{formatDate(order.updated_at)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Workers */}
            {order.workers.length > 0 && (
              <Card>
                <CardHeader className='pb-3'>
                  <CardTitle className='flex items-center gap-2 text-sm font-medium'>
                    <User className='h-4 w-4' />
                    {t('orders.assignedWorkers')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='flex flex-wrap gap-2'>
                    {order.workers.map((worker) => (
                      <Badge key={worker.id} variant='secondary'>
                        {worker.first_name} {worker.last_name}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : notFound ? (
          <Card>
            <CardContent className='py-12 text-center'>
              <div className='flex flex-col items-center gap-4'>
                <div className='flex h-16 w-16 items-center justify-center rounded-full bg-muted'>
                  <PackageX className='h-8 w-8 text-muted-foreground' />
                </div>
                <div className='space-y-2'>
                  <h3 className='text-lg font-semibold'>{t('orders.orderNotFound')}</h3>
                  <p className='text-sm text-muted-foreground'>
                    {t('orders.orderNotFoundDesc')}
                  </p>
                </div>
                <Button variant='outline' asChild className='mt-2'>
                  <Link to='/orders'>
                    <ArrowLeft className='mr-2 h-4 w-4' />
                    {t('orders.backToOrders')}
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('orders.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('orders.deleteConfirmDesc', { id: order?.id })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className='bg-destructive hover:bg-destructive/90 cursor-pointer'
            >
              {isDeleting ? t('common.deleting') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
